import { useCallback, useEffect, useState } from "react";
import { StartCondFn, SwiperCallbackFn, ThresholdCondFn } from "./types";

const EXCLUDE_TAGS = ["input", "textarea"];

const useSwipe = (
  startCond: StartCondFn,
  thresholdCond: ThresholdCondFn,
  fn: SwiperCallbackFn
): void => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startX, setStartX] = useState<number>(0);

  const onDragStart = (e: TouchEvent): void => {
    if (EXCLUDE_TAGS.includes((e.target as Element).tagName.toLowerCase()))
      return;
    e.preventDefault();
    if (!startCond(e.touches[0].clientX)) return;
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const onDrag = useCallback(
    (e: TouchEvent): void => {
      if (!isDragging) return;
      if (thresholdCond(startX, e.touches[0].clientX)) {
        fn();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDragging, startX]
  );

  const onDragEnd = (e: TouchEvent): void => {
    setIsDragging(false);
  };

  useEffect(() => {
    window.addEventListener("touchstart", onDragStart);
    window.addEventListener("touchmove", onDrag);
    window.addEventListener("touchend", onDragEnd);
    return (): void => {
      window.removeEventListener("touchstart", onDragStart);
      window.removeEventListener("touchmove", onDrag);
      window.removeEventListener("touchend", onDragEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDrag]);

  const onDragStartWin = (e: MouseEvent): void => {
    if (EXCLUDE_TAGS.includes((e.target as Element).tagName.toLowerCase()))
      return;
    e.preventDefault();
    if (!startCond(e.clientX)) return;
    setStartX(e.clientX);
    setIsDragging(true);
  };

  const onDragWin = useCallback(
    (e: MouseEvent): void => {
      if (!isDragging) return;
      if (thresholdCond(startX, e.clientX)) {
        fn();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDragging, startX]
  );

  const onDragEndWin = (e: MouseEvent): void => {
    setIsDragging(false);
  };

  useEffect(() => {
    window.addEventListener("mousedown", onDragStartWin);
    window.addEventListener("mousemove", onDragWin);
    window.addEventListener("mouseup", onDragEndWin);
    return (): void => {
      window.removeEventListener("mousedown", onDragStartWin);
      window.removeEventListener("mousemove", onDragWin);
      window.removeEventListener("mouseup", onDragEndWin);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDragWin]);
};

export default useSwipe;
